// App.tsx
import React, { useState } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import Stack from "@mui/material/Stack";
import AppBar from "@mui/material/AppBar";
import { useAuth } from "react-oidc-context";
import Toolbar from "@mui/material/Toolbar";
import Typography from '@mui/material/Typography';
import Box from "@mui/material/Box";
import { useTheme, useMediaQuery } from "@mui/material";
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Divider from '@mui/material/Divider';

import AppContext, { AppContextType } from "./AppContext";

// Import authentication related components
import ProtectedRoute from "./ProtectedRoute";
import Callback from "./Callback";
import PublicLogout from "./PublicLogout";

import Home from "./Home";
import Admin from "./Admin";
import Login from "./Login";
import ChatPage from "./ChatPage";
import Network from "./Network";
import TenderQPage from "./TenderQPage";
import InstallSurvey from "./InstallSurvey";
import ProposalDraftPage from './ProposalDraftPage';
import ReviewAuditPage from './ReviewAuditPage';
import CompanyOnboardingPage from './CompanyOnboardingPage';
import AlertsBell from './AlertsBell';

// Define a type for the user profile from Cognito, adjust as needed
interface UserProfile {
  "cognito:groups"?: string[];
  [key: string]: any;
}

interface ChatMessage {
  text: string;
  isBot: boolean;
  chartImage?: string;
  questionCategory?: string;
  embeddedQuestion?: string;
}

function App() {
    const navigate = useNavigate();
    const theme = useTheme();
    const auth = useAuth();
    const [userProfile, setUserProfile] = React.useState<UserProfile | null>(null);
    const [surveyResult, setSurveyResult] = React.useState<any[]>([]);
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
    const [chatHistory, setChatHistory] = useState<any[]>([]);
    const [submittedFeedback, setSubmittedFeedback] = useState<number[]>([]);
    const [currentSubject, setCurrentSubject] = useState<string>("glueup");


    const desktopMenuFontSize = "0.875rem";
    const mobileMenuFontSize = "0.75rem";
    const appBarHeight = (theme.mixins.toolbar as { minHeight?: number }).minHeight ?? 56;
    
    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    React.useEffect(() => {
      if (auth.isAuthenticated && auth.user?.profile) {
        const profileData = auth.user.profile as UserProfile;
        const userGroups = auth.user.profile["cognito:groups"] as string[] | undefined;

        setUserProfile({
          ...profileData,
          "cognito:groups": userGroups,
        });
      } else {
        setUserProfile(null);
      }
    }, [auth.isAuthenticated, auth.user?.profile]);

    // --- Navigation Handlers (Unchanged) ---
    const handleNavigateHome = () => navigate(`/`);
    const handleNavigatePrompt = () => navigate(`/prompt`);
    const handleNavigateTenderQ = () => navigate(`/tenderq`);
    const handleNavigateNetwork = () => navigate(`/network`);
    const handleNavigateInstallSurvey = () => navigate(`/install-survey`);
    const handleNavigateAdmin = () => {
      const userGroups = userProfile?.["cognito:groups"] || [];
      if (auth.isAuthenticated && 
          (userGroups.includes('admin') || userGroups.includes('quantum'))
      ) {
        navigate(`/admin`);
      } else {
        navigate('/login');
      }
    };
    const handleNavigateLogin = () => navigate(`/login`);
    const handleLogout = () => navigate("/public-logout");

    const drawerItems = (
        <Box
            sx={{ width: 250 }}
            role="presentation"
        >
            <List>
                <ListItem disablePadding>
                    <ListItemButton onClick={() => { handleNavigateHome(); handleDrawerToggle(); }}>
                        <ListItemText primary="Home" sx={{ '& .MuiTypography-root': { fontSize: mobileMenuFontSize } }} />
                    </ListItemButton>
                </ListItem>

                {auth.isAuthenticated &&
                    userProfile && userProfile["cognito:groups"] &&
                    (userProfile["cognito:groups"].includes('admin') || userProfile["cognito:groups"].includes('quantum')) && (
                    <ListItem disablePadding>
                        <ListItemButton onClick={() => { handleNavigateAdmin(); handleDrawerToggle(); }}>
                            <ListItemText primary="Admin" sx={{ '& .MuiTypography-root': { fontSize: mobileMenuFontSize } }} />
                        </ListItemButton>
                    </ListItem>
                )}

                <ListItem disablePadding>
                    <ListItemButton onClick={() => { handleNavigatePrompt(); handleDrawerToggle(); }}>
                        <ListItemText primary="Prompt" sx={{ '& .MuiTypography-root': { fontSize: mobileMenuFontSize } }} />
                    </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                    <ListItemButton onClick={() => { handleNavigateTenderQ(); handleDrawerToggle(); }}>
                        <ListItemText primary="TenderQ" sx={{ '& .MuiTypography-root': { fontSize: mobileMenuFontSize } }} />
                    </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                    <ListItemButton onClick={() => { handleNavigateNetwork(); handleDrawerToggle(); }}>
                        <ListItemText primary="Network" sx={{ '& .MuiTypography-root': { fontSize: mobileMenuFontSize } }} />
                    </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                    <ListItemButton onClick={() => { handleNavigateInstallSurvey(); handleDrawerToggle(); }}>
                        <ListItemText primary="Install Survey" sx={{ '& .MuiTypography-root': { fontSize: mobileMenuFontSize } }} />
                    </ListItemButton>
                </ListItem>

                <Divider sx={{ my: 1 }} />

                {auth.isAuthenticated && userProfile ? (
                    <ListItem disablePadding>
                        <ListItemButton onClick={() => { handleLogout(); handleDrawerToggle(); }}>
                            <ListItemText 
                                primary={`Logout (${userProfile.preferred_username || userProfile.username || userProfile["cognito:username"] || userProfile.email || 'User'})`} 
                                sx={{ '& .MuiTypography-root': { fontSize: mobileMenuFontSize } }}
                            />
                        </ListItemButton>
                    </ListItem>
                ) : (
                    <ListItem disablePadding>
                        <ListItemButton onClick={() => { handleNavigateLogin(); handleDrawerToggle(); }}>
                            <ListItemText primary="Login" sx={{ '& .MuiTypography-root': { fontSize: mobileMenuFontSize } }} />
                        </ListItemButton>
                    </ListItem>
                )}
            </List>
        </Box>
    );

    const contextValue: AppContextType = {
        user: userProfile,
        setUser: setUserProfile,
        surveyResult,
        setSurveyResult,
        chatMessages,
        setChatMessages,
        chatHistory,
        setChatHistory,
        submittedFeedback,
        setSubmittedFeedback,
        currentSubject,
        setCurrentSubject,
    };

    return (
        <Box sx={{ display: 'flex', height: '100vh', flexDirection: 'column' }}>
            <AppContext.Provider value={contextValue}>
                <AppBar
                    position="fixed"
                    color="default"
                    elevation={1}
                    sx={{ zIndex: theme.zIndex.drawer + 1 }}
                >
                    <Toolbar sx={{ minHeight: `${appBarHeight}px` }}>
                       <Stack direction="row" sx={{ justifyContent: "space-between", width: "100%", alignItems: "center" }}>
                           <Stack direction="row" sx={{ alignItems: "center", gap: { xs: 1, sm: 2 } }}>
                               {isSmallScreen && (
                                   <IconButton
                                       color="inherit"
                                       aria-label="open drawer"
                                       edge="start"
                                       onClick={handleDrawerToggle}
                                       sx={{ mr: 0.5 }} 
                                   >
                                       <MenuIcon />
                                   </IconButton>
                               )}
                               <Typography variant="h6" noWrap component="a" onClick={handleNavigateHome} sx={{ fontFamily: "Comic Sans MS", fontWeight: 500, color: "inherit", textDecoration: "none", display: "flex", alignItems: "center", cursor: 'pointer', '&:hover': { color: 'primary.main' } }}>
                                   <img src="/icon.png" alt="QA Bot icon" height="25em" style={{ paddingRight: "0.3em", verticalAlign: 'middle' }} /> QA Bot
                               </Typography>
                               {!isSmallScreen && (
                                   <>
                                       {auth.isAuthenticated &&
                                           userProfile && userProfile["cognito:groups"] &&
                                           (userProfile["cognito:groups"].includes('admin') || userProfile["cognito:groups"].includes('quantum')) && (
                                           <Typography variant="button" component="a" onClick={handleNavigateAdmin} sx={{ fontSize: desktopMenuFontSize, color: "inherit", textDecoration: "none", cursor: 'pointer', '&:hover': { color: 'primary.main' } }}>Admin</Typography>
                                       )}
                                       <Typography variant="button" component="a" onClick={handleNavigatePrompt} sx={{ fontSize: desktopMenuFontSize, color: "inherit", textDecoration: "none", cursor: 'pointer', '&:hover': { color: 'primary.main' } }}>Prompt</Typography>
                                       <Typography variant="button" component="a" onClick={handleNavigateTenderQ} sx={{ fontSize: desktopMenuFontSize, color: "inherit", textDecoration: "none", cursor: 'pointer', '&:hover': { color: 'primary.main' } }}>TenderQ</Typography>
                                       <Typography variant="button" component="a" onClick={handleNavigateNetwork} sx={{ fontSize: desktopMenuFontSize, color: "inherit", textDecoration: "none", cursor: 'pointer', '&:hover': { color: 'primary.main' } }}>Network</Typography>
                                       <Typography variant="button" component="a" onClick={handleNavigateInstallSurvey} sx={{ fontSize: desktopMenuFontSize, color: "inherit", textDecoration: "none", cursor: 'pointer', '&:hover': { color: 'primary.main' } }}>QSurvey</Typography>
                                   </>
                               )}
                           </Stack>
                           <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
                               {/* Personal match alerts (off / ≥ 80 % / ≥ 90 %): next to the login, on every screen size. */}
                               {auth.isAuthenticated && userProfile && <AlertsBell auth={auth} />}
                               {!isSmallScreen && (
                                   auth.isAuthenticated && userProfile ? (
                                       <Typography variant="button" component="a" onClick={handleLogout} sx={{ fontSize: desktopMenuFontSize, color: "inherit", textDecoration: "none", cursor: 'pointer', '&:hover': { color: 'primary.main' } }}>
                                           Logout ({ `${userProfile["cognito:groups"]?.[0] || 'user'}:${userProfile.preferred_username || userProfile.username || userProfile["cognito:username"] || userProfile.email || 'User'}` })
                                       </Typography>
                                   ) : (
                                       <Typography variant="button" component="a" onClick={handleNavigateLogin} sx={{ fontSize: desktopMenuFontSize, color: "inherit", textDecoration: "none", cursor: 'pointer', '&:hover': { color: 'primary.main' } }}>Login</Typography>
                                   )
                               )}
                           </Stack>
                       </Stack>
                    </Toolbar>
                </AppBar>

                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        display: { xs: 'block', sm: 'none' },
                        '& .MuiDrawer-paper': { 
                            boxSizing: 'border-box', 
                            width: 250, 
                            pt: `${appBarHeight}px`
                        },
                    }}
                >
                    {drawerItems}
                </Drawer>

                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        pt: `${appBarHeight}px`,
                        bgcolor: 'background.default',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <Routes>
                       <Route path="/login" element={<Login />} />
                       <Route path="/callback" element={<Callback />} />
                       <Route path="/public-logout" element={<PublicLogout />} />
                       <Route element={<ProtectedRoute />}>
                         <Route path="/" element={<Home />} />
                         <Route path="/admin" element={<Admin />} />
                         <Route path="/prompt" element={<ChatPage />} />
                         <Route path="/tenderq" element={<TenderQPage />} />
                         <Route path="/network" element={<Network />} />
                         <Route path="/install-survey" element={<InstallSurvey />} />
                         <Route path="/proposals/new/:tenderId" element={<ProposalDraftPage />} />
                         <Route path="/reviews/new/:tenderId" element={<ReviewAuditPage />} />
                         <Route path="/reviews/:reviewId/audit" element={<ReviewAuditPage />} />
                         <Route path="/onboarding" element={<CompanyOnboardingPage />} />
                       </Route>
                    </Routes>
                </Box>
            </AppContext.Provider>
        </Box>
    );
}

export default App;